import React, { useState, useEffect } from 'react';
import { FaSearch, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import api from '../services/api';

const AllDataPoints = () => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [dataPoints, setDataPoints] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Nowe stany
    const [deviceIdToDeviceName, setDeviceIdToDeviceName] = useState({});
    const [deviceIdToUserId, setDeviceIdToUserId] = useState({});
    const [userIdToUsername, setUserIdToUsername] = useState({});

    useEffect(() => {
        // Funkcja do pobrania urządzeń i użytkowników
        const fetchDevicesAndUsers = async () => {
            try {
                // Pobieramy urządzenia
                const devicesResponse = await api.get('/devices/');
                const devices = devicesResponse.data;

                const deviceNameMap = {};
                const deviceUserMap = {};

                devices.forEach((device) => {
                    deviceNameMap[device.id] = device.name;
                    deviceUserMap[device.id] = device.user_id;
                });

                setDeviceIdToDeviceName(deviceNameMap);
                setDeviceIdToUserId(deviceUserMap);

                // Pobieramy użytkowników
                const usersResponse = await api.get('/users/');
                const users = usersResponse.data;

                const userNameMap = {};
                users.forEach((user) => {
                    userNameMap[user.id] = user.username;
                });

                setUserIdToUsername(userNameMap);
            } catch (error) {
                console.error('Error fetching devices or users', error);
                setError('Nie udało się pobrać danych o urządzeniach lub użytkownikach');
            }
        };

        fetchDevicesAndUsers();
    }, []);

    const handleFetchData = async (pageNumber = page) => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get('/data-points/all', {
                params: {
                    start_date: startDate,
                    end_date: endDate,
                    page: pageNumber,
                    size: pageSize,
                },
            });
            setDataPoints(response.data.items);
            setTotalPages(response.data.pages);
        } catch (error) {
            console.error('Error fetching data points', error);
            setError('Nie udało się pobrać danych. Sprawdź wybrane daty i spróbuj ponownie.');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        handleFetchData(newPage);
    };

    // Renderowanie karty dla widoku mobilnego
    const renderMobileDataPoints = () => {
        return dataPoints.map((dataPoint) => {
            const deviceName = deviceIdToDeviceName[dataPoint.device_id] || '...';
            const userId = deviceIdToUserId[dataPoint.device_id];
            const username = userIdToUsername[userId] || '...';
            
            return (
                <div key={dataPoint.id} className="mb-4 p-4 border border-gray-200 rounded-lg bg-white shadow-sm">
                    <div className="mb-2">
                        <span className="font-bold">PPE:</span> {deviceName}
                    </div>
                    <div className="mb-2">
                        <span className="font-bold">Użytkownik:</span> {username}
                    </div>
                    <div className="mb-2">
                        <span className="font-bold">Czas:</span> {new Date(dataPoint.timestamp).toLocaleString()}
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-3">
                        <div className="text-center">
                            <div className="text-xs text-gray-500">Produkcja</div>
                            <div className="font-semibold">{dataPoint.production}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-xs text-gray-500">Zużycie</div>
                            <div className="font-semibold">{dataPoint.consumption}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-xs text-gray-500">Bilans</div>
                            <div className="font-semibold">{dataPoint.balance}</div>
                        </div>
                    </div>
                </div>
            );
        });
    };

    // Renderowanie tradycyjnej tabeli dla większych ekranów
    const renderDesktopTable = () => {
        return (
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200">
                    <thead className="bg-gray-50">
                    <tr>
                        <th className="border px-4 py-2">Nazwa PPE</th>
                        <th className="border px-4 py-2">Nazwa użytkownika</th>
                        <th className="border px-4 py-2">Znacznik czasu</th>
                        <th className="border px-4 py-2">Produkcja</th>
                        <th className="border px-4 py-2">Zużycie</th>
                        <th className="border px-4 py-2">Bilans</th>
                    </tr>
                    </thead>
                    <tbody>
                    {dataPoints.map((dataPoint) => {
                        const deviceName = deviceIdToDeviceName[dataPoint.device_id] || '...';
                        const userId = deviceIdToUserId[dataPoint.device_id];
                        const username = userIdToUsername[userId] || '...';

                        return (
                            <tr key={dataPoint.id}>
                                <td className="border px-4 py-2">{deviceName}</td>
                                <td className="border px-4 py-2">{username}</td>
                                <td className="border px-4 py-2">
                                    {new Date(dataPoint.timestamp).toLocaleString()}
                                </td>
                                <td className="border px-4 py-2">{dataPoint.production}</td>
                                <td className="border px-4 py-2">{dataPoint.consumption}</td>
                                <td className="border px-4 py-2">{dataPoint.balance}</td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
        );
    };

    // Renderowanie paginacji
    const renderPagination = () => {
        return (
            <div className="mt-4 flex justify-between items-center">
                <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1 || loading}
                    className="bg-softTeal text-pureWhite font-semibold px-4 py-2 rounded-lg hover:bg-warmOrange transition flex items-center"
                >
                    <FaArrowLeft className="mr-2" /> Poprzednia
                </button>
                <span className="text-gray-700">
                    Strona {page} z {totalPages}
                </span>
                <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages || loading}
                    className="bg-softTeal text-pureWhite font-semibold px-4 py-2 rounded-lg hover:bg-warmOrange transition flex items-center"
                >
                    Następna <FaArrowRight className="ml-2" />
                </button>
            </div>
        );
    };

    return (
        <div className="min-h-screen flex flex-col items-center p-4 sm:p-6">
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg max-w-full w-full">
                <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Zatwierdzone dane</h2>

                {/* Filtr daty - responsywny układ */}
                <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="start-date" className="block text-gray-700 mb-2">
                            Data początkowa:
                        </label>
                        <input
                            type="date"
                            id="start-date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                        />
                    </div>

                    <div>
                        <label htmlFor="end-date" className="block text-gray-700 mb-2">
                            Data końcowa:
                        </label>
                        <input
                            type="date"
                            id="end-date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                        />
                    </div>
                </div>

                <button
                    onClick={() => handleFetchData()}
                    className="bg-softTeal text-pureWhite font-semibold px-4 py-2 mb-6 rounded-lg hover:bg-warmOrange transition flex items-center justify-center w-full sm:w-auto"
                    disabled={loading}
                >
                    {loading ? 'Ładowanie...' : (
                        <>
                            <FaSearch className="mr-2" /> Wyświetl dane
                        </>
                    )}
                </button>

                {error && (
                    <div className="text-red-500 mb-4 p-3 bg-red-50 rounded-lg">
                        {error}
                    </div>
                )}

                {dataPoints.length > 0 && !loading && (
                    <>
                        <div className="md:hidden">
                            {renderMobileDataPoints()}
                            {totalPages > 1 && renderPagination()}
                        </div>
                        
                        <div className="hidden md:block">
                            {renderDesktopTable()}
                            {totalPages > 1 && renderPagination()}
                        </div>
                    </>
                )}

                {dataPoints.length === 0 && !loading && !error && (
                    <p className="text-gray-500 mt-6 text-center">
                        Nie znaleziono punktów danych dla wybranego zakresu dat.
                    </p>
                )}

                {loading && (
                    <p className="text-center py-6">
                        Ładowanie danych...
                    </p>
                )}
            </div>
        </div>
    );
};

export default AllDataPoints;
